import React, { useState } from 'react';

export interface FemParams {
  femRampLevels:        number;
  femRampCheckPeriod:   number;
  femBendingFactor:     number;
  femBoostFactor:       number;
  femMaterialFailRatio: number;
}

const FEM_DEFAULTS: FemParams = {
  femRampLevels:        100,
  femRampCheckPeriod:   400,
  femBendingFactor:     0.0,
  femBoostFactor:       1.05,
  femMaterialFailRatio: 8.0,
};

interface Props {
  values:  FemParams;
  onSave:  (v: FemParams) => void;
  onClose: () => void;
}

interface NumFieldProps {
  label: string;
  value: number;
  min?:  number;
  max?:  number;
  step?: number;
  onChange: (v: number) => void;
}

const NumField: React.FC<NumFieldProps> = ({ label, value, min = 0, max, step = 1, onChange }) => (
  <div className="param-field">
    <label className="param-label">{label}</label>
    <input
      type="number"
      className="param-input"
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={e => {
        const v = parseFloat(e.target.value);
        if (!isNaN(v) && v >= (min ?? -Infinity)) onChange(v);
      }}
    />
  </div>
);

const FemAdvancedDialog: React.FC<Props> = ({ values, onSave, onClose }) => {
  const [loc, setLoc] = useState<FemParams>({ ...values });
  const set = <K extends keyof FemParams>(k: K, v: FemParams[K]) =>
    setLoc(prev => ({ ...prev, [k]: v }));

  return (
    <div className="layers-overlay fem-adv-overlay">
      <div className="param-dialog fem-adv-dialog">

        <div className="param-title">FEM Solver – Advanced</div>

        <div className="param-section">Pressure ramp (quasi-static startup):</div>
        <div className="param-row2">
          <NumField label="Ramp levels:" value={loc.femRampLevels}
            min={10} step={10}
            onChange={v => set('femRampLevels', Math.round(v))} />
          <NumField label="Steps/level:" value={loc.femRampCheckPeriod}
            min={50} step={50}
            onChange={v => set('femRampCheckPeriod', Math.round(v))} />
        </div>
        <div className="param-hint">
          Total ramp steps: {loc.femRampLevels * loc.femRampCheckPeriod}
          &nbsp;|&nbsp; recommended: 40 000 – 80 000
        </div>

        <div className="param-section">Bending stiffness:</div>
        <NumField label="Bending factor (0 = membrane, 1 = physical):" value={loc.femBendingFactor}
          min={0} max={10000} step={1}
          onChange={v => set('femBendingFactor', Math.max(0, v))} />
        <div className="param-hint">
          0 = pure membrane (correct for thin films); 1 = physical bending stiffness;
          10–1000 = suppresses flat-top tendency (slightly less physical)
        </div>

        <div className="param-section">Pressure boost (after equilibrium, before target):</div>
        <div className="param-row2">
          <NumField label="Boost factor:" value={loc.femBoostFactor}
            min={1.01} max={1.5} step={0.01}
            onChange={v => set('femBoostFactor', Math.min(1.5, Math.max(1.01, v)))} />
          <NumField label="Fail ratio:" value={loc.femMaterialFailRatio}
            min={2} max={50} step={0.5}
            onChange={v => set('femMaterialFailRatio', Math.max(2, v))} />
        </div>
        <div className="param-hint">
          Boost factor: pressure multiplier per equilibrium cycle (1.05 = +5%).
          Fail ratio: triangle area ratio limit before stopping (area × ratio → thickness / ratio).
        </div>

        <div className="param-gap" />
        <button className="param-btn-reset-fem"
          onClick={() => setLoc({ ...FEM_DEFAULTS })}>
          Reset to defaults
        </button>
        <div className="param-gap" />
        <button onClick={onClose}>Cancel</button>
        <div className="param-gap" />
        <button onClick={() => { onSave(loc); onClose(); }}>OK</button>

      </div>
    </div>
  );
};

export default FemAdvancedDialog;
